import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaCopy, FaExternalLinkAlt, FaEye } from "react-icons/fa";
import { FiCopy } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

const AdmissionManager = () => {
  const navigate = useNavigate();
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({});
  const [totalItems, setTotalItems] = useState(0);
  const [userRole, setUserRole] = useState(null);

  const fetchAdmissions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/admissions`, {
        params: {
          search: searchQuery,
          paymentStatus: statusFilter,
          page: currentPage,
          limit: rowsPerPage
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        },
      });

      setAdmissions(response.data.data || []);
      setPagination(response.data.pagination || {});
      setTotalItems(response.data.pagination?.totalRecords || 0);
    } catch (error) {
      toast.error("Failed to fetch admissions");
      console.error("Error fetching admissions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("userData")) {
      setUserRole(JSON.parse(localStorage.getItem("userData")));
    }
    fetchAdmissions();
  }, []);

  useEffect(() => {
    fetchAdmissions();
  }, [searchQuery, statusFilter, currentPage]);

  const handleDelete = async (admissionId) => {
    if (window.confirm("Are you sure you want to delete this admission form?")) {
      try {
        await axios.delete(`${process.env.REACT_APP_BASE_URL}/admission/${admissionId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${localStorage.getItem("token")}`,
          },
        });
        toast.success("Admission deleted successfully");
        fetchAdmissions();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete admission");
        console.error("Error deleting admission:", error);
      }
    }
  };

  const copyToClipboard = async (collegeName, slug) => {
    const collegeSlug = collegeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    const url = `https://admissiondekhoo.com/admission-form/${collegeSlug}/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      toast("URL copied to clipboard!");
      setTimeout(() => setCopiedSlug(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL: ', err);
      toast("Failed to copy URL");
    }
  };

  const handleCopyAdmissionData = (admission) => {
    // Create a simplified copy of admission data with readable information
    const simplifiedAdmission = `Name: ${admission.studentName || ""}
Email: ${admission.email || ""}
Mobile: ${admission.mobile || ""}
Father Name: ${admission.fatherName || ""}
Father Mobile: ${admission.fatherMobile || ""}
College: ${admission.collegeName || ""}
Address: ${admission.address || ""}
Aadhar Card: ${admission.aadharCard || ""}
10th Percentage: ${admission.tenthPercentage || ""}
12th Percentage: ${admission.twelfthPercentage || ""}
Graduation Percentage: ${admission.graduationPercentage || ""}
ITI/Diploma: ${admission.itiDiploma || ""}
Courses: ${admission.selectedCourses?.map(course => course.name || course).join(", ") || ""}`;

    navigator.clipboard.writeText(simplifiedAdmission).then(() => {
      toast("Admission data copied to clipboard!");
    }).catch(err => {
      console.error('Failed to copy admission data: ', err);
      toast("Failed to copy admission data");
    });
  };

  const canEditOrDelete = (admission) => {
    return admission.paymentInfo?.status === "pending";
  };

  return (
    <div className="flex min-h-screen bg-gray">
      <Sidebar />
      <ToastContainer />
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray">Admission Manager</h1>
            </div>

            <div className="mb-6">
              <div className="flex gap-4 items-center">
                <input
                  type="text"
                  placeholder="Search by student name or college..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="flex-1 px-4 py-2 border border-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-blue"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-blue"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                </select>
                <button
                  onClick={() => navigate("/add-admission")}
                  className="flex items-center gap-2 bg-blue text-white px-4 py-2 rounded-lg hover:bg-blue transition-colors"
                >
                  <FaPlus />
                  Add Admission
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <p className="mt-2 text-gray-600">Loading admissions...</p>
              </div>
            ) : admissions.length === 0 ? (
              <div className="text-center p-8 text-gray">
                No admissions found
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray border-b">
                        <th className="text-left p-3 font-semibold text-gray">S.No</th>
                        <th className="text-left p-3 font-semibold text-gray">Student Name</th>
                        <th className="text-left p-3 font-semibold text-gray">Mobile</th>
                        <th className="text-left p-3 font-semibold text-gray">College</th>
                        <th className="text-left p-3 font-semibold text-gray">Courses</th>
                        <th className="text-left p-3 font-semibold text-gray">Payment Status</th>
                        <th className="text-left p-3 font-semibold text-gray">Created</th>
                        <th className="text-left p-3 font-semibold text-gray">Copy Data</th>
                        <th className="text-left p-3 font-semibold text-gray">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admissions.map((admission, index) => (
                        <tr key={admission._id} className="border-b hover:bg-gray">
                          <td className="p-3 text-gray">
                            {(currentPage - 1) * rowsPerPage + index + 1}
                          </td>
                          <td className="p-3 text-gray font-medium">
                            {admission.studentName || 'N/A'}
                          </td>
                          <td className="p-3 text-gray">
                            {admission.mobile || 'N/A'}
                          </td>
                          <td className="p-3 text-gray">
                            {admission.collegeName || 'N/A'}
                          </td>
                          <td className="p-3 text-gray">
                            {admission.selectedCourses?.length > 0
                              ? admission.selectedCourses.map(course => course.name).join(', ')
                              : 'N/A'
                            }
                          </td>
                          <td className="p-3">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${admission.paymentInfo?.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : admission.paymentInfo?.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : admission.paymentInfo?.status === 'failed'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                              {admission.paymentInfo?.status || 'N/A'}
                            </span>
                          </td>
                          <td className="p-3 text-gray">
                            {new Date(admission.submittedAt || admission.createdAt)?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                          </td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() => handleCopyAdmissionData(admission)}
                              className="text-blue-500 hover:text-blue-700 transition-colors"
                              title="Copy admission data"
                            >
                              <FiCopy />
                            </button>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => navigate(`/view-admission/${admission._id}`)}
                                className="text-blue hover:text-blue transition-colors"
                                title="View"
                              >
                                <FaEye />
                              </button>
                              <button
                                onClick={() => copyToClipboard(admission.collegeName, admission.slug)}
                                className="text-blue hover:text-blue transition-colors"
                                title="Copy URL"
                              >
                                {copiedSlug === admission.slug ? (
                                  <span className="text-green text-sm">Copied!</span>
                                ) : (
                                  <FaCopy />
                                )}
                              </button>
                              {canEditOrDelete(admission) && (
                                <>
                                  <button
                                    onClick={() => navigate(`/edit-admission/${admission._id}`)}
                                    className="text-blue hover:text-blue transition-colors"
                                    title="Edit"
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(admission._id)}
                                    className="text-red hover:text-red transition-colors"
                                    title="Delete"
                                  >
                                    <FaTrash />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pagination.totalPages > 1 && (
                  <div className="flex justify-center mt-6 gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={!pagination.hasPrevPage}
                      className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1">
                      Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalRecords} items)
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                      disabled={!pagination.hasNextPage}
                      className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionManager;
